import { useSessionKey } from "@auth/sessionStore";
import { StaffPhotoCapture } from "@perception/StaffPhotoCapture";
import { useRoomsListQuery } from "@rooms/roomsQueries";
import {
  cn,
  EmptyNote,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  PanelError,
  SkeletonStack,
  Tag,
} from "@scribenote/brian-design-system";
import type { StaffCoverage } from "@users/staffPhotoClient";
import { useStaffPhotoCoverage } from "@users/staffPhotoQueries";
import { useState } from "react";

interface Counts {
  confirmed: number;
  pending: number;
}

const countsIn = (person: StaffCoverage, roomId: string): Counts => {
  const row = person.roomPhotos.find((photos) => photos.roomId === roomId);
  return { confirmed: row?.confirmed ?? 0, pending: row?.pending ?? 0 };
};

/** Photos from no listed column: a room without a camera feed, or a capture that did not record its room. */
const otherCounts = (person: StaffCoverage, roomIds: Set<string>): Counts =>
  person.roomPhotos
    .filter((photos) => photos.roomId === null || !roomIds.has(photos.roomId))
    .reduce(
      (sum, photos) => ({
        confirmed: sum.confirmed + photos.confirmed,
        pending: sum.pending + photos.pending,
      }),
      { confirmed: 0, pending: 0 },
    );

const countsText = ({ confirmed, pending }: Counts) =>
  [
    confirmed > 0 ? `${confirmed} confirmed` : null,
    pending > 0 ? `${pending} pending` : null,
  ]
    .filter(Boolean)
    .join(", ") || "None";

/**
 * Who has reference photos from which room camera. Counts only; an image is
 * shown to its subject alone. A cell opens the capture dialog for that room
 * and person.
 */
export const ReferencePhotosPanel = ({ active }: { active: boolean }) => {
  const sessionKey = useSessionKey();
  const rooms = useRoomsListQuery(sessionKey, true, active);
  const coverage = useStaffPhotoCoverage(sessionKey, active);
  const [capturing, setCapturing] = useState<{
    roomId: string;
    userId: string;
  } | null>(null);

  if (rooms.isError)
    return (
      <PanelError label="the rooms" onRetry={() => void rooms.refetch()} />
    );
  if (coverage.isError)
    return (
      <PanelError
        label="who has reference photos"
        onRetry={() => void coverage.refetch()}
      />
    );
  if (!rooms.data || !coverage.data)
    return <SkeletonStack count={3} itemClassName="h-10" />;

  const { roomsWithStills, staff } = coverage.data;
  const columns = rooms.data.filter((room) =>
    roomsWithStills.includes(room.id),
  );
  const columnIds = new Set(columns.map((room) => room.id));

  return (
    <div className="flex flex-col gap-3 font-body">
      <p className="text-sm text-ink-body">
        Brian names staff from reference photos cropped from the room&rsquo;s
        own camera. Choose a cell to capture photos of that person from that
        room.
      </p>
      {columns.length === 0 ? (
        <EmptyNote className="p-6">
          No room has sent a camera still yet.
        </EmptyNote>
      ) : staff.length === 0 ? (
        <EmptyNote className="p-6">No staff yet.</EmptyNote>
      ) : (
        <LedgerTable>
          <LedgerHead>
            <LedgerRow>
              <LedgerHeadCell>Staff</LedgerHeadCell>
              <LedgerHeadCell>Agreed</LedgerHeadCell>
              <LedgerHeadCell>Guided</LedgerHeadCell>
              {columns.map((room) => (
                <LedgerHeadCell key={room.id}>{room.name}</LedgerHeadCell>
              ))}
              <LedgerHeadCell>Other</LedgerHeadCell>
            </LedgerRow>
          </LedgerHead>
          <LedgerBody>
            {staff.map((person) => {
              const other = otherCounts(person, columnIds);
              return (
                <LedgerRow key={person.userId}>
                  <LedgerCell>
                    <span className="block max-w-[16rem] truncate font-medium text-ink">
                      {person.displayName}
                    </span>
                  </LedgerCell>
                  <LedgerCell>
                    <Tag tone={person.consented ? "emphasis" : "neutral"}>
                      {person.consented ? "Yes" : "No"}
                    </Tag>
                  </LedgerCell>
                  <LedgerCell mono className="text-annotation">
                    {person.guidedPhotos}
                  </LedgerCell>
                  {columns.map((room) => {
                    const counts = countsIn(person, room.id);
                    const empty = counts.confirmed + counts.pending === 0;
                    return (
                      <LedgerCell key={room.id}>
                        <button
                          type="button"
                          aria-label={`Capture ${person.displayName} in ${room.name}: ${countsText(counts)}`}
                          onClick={() =>
                            setCapturing({
                              roomId: room.id,
                              userId: person.userId,
                            })
                          }
                          className={cn(
                            "w-full min-w-[7rem] rounded-paper border px-2 py-1 text-left text-sm transition duration-150 ease-paper hover:bg-smudge focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                            empty
                              ? "border-dashed border-rule-strong text-faint"
                              : "border-rule text-ink",
                          )}
                        >
                          {countsText(counts)}
                        </button>
                      </LedgerCell>
                    );
                  })}
                  <LedgerCell className="whitespace-nowrap text-annotation">
                    {countsText(other)}
                  </LedgerCell>
                </LedgerRow>
              );
            })}
          </LedgerBody>
        </LedgerTable>
      )}
      <p className="text-sm text-annotation">
        Guided counts the person&rsquo;s own self-photos. Other counts photos
        from a room with no camera feed, or an earlier capture whose room was
        not recorded.
      </p>
      {capturing && (
        <StaffPhotoCapture
          roomId={capturing.roomId}
          userId={capturing.userId}
          onClose={() => setCapturing(null)}
        />
      )}
    </div>
  );
};
